import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { getAnonPins, clearAnonPins } from "./files";

const AuthContext = createContext({
  user: null,
  loading: true,
  signIn: () => {},
  signOut: () => {},
  refresh: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me", { withCredentials: true });
      setUser(r.data);
      // Claim any pins this person made while signed out
      const anon = getAnonPins();
      if (anon.length > 0) {
        try {
          await api.post("/me/pins/claim", { file_ids: anon }, { withCredentials: true });
        } catch (e) {
          /* ignore — pins stay in localStorage for the next sign-in */
        }
        clearAnonPins();
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me — AuthCallback will exchange session_id first
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const signOut = async () => {
    try {
      await api.post("/auth/logout", null, { withCredentials: true });
    } catch (e) {
      /* ignore */
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

/** AuthCallback — handles the #session_id=... fragment returned by Emergent OAuth. */
export function useAuthCallback() {
  const { refresh } = useAuth();
  const [state, setState] = useState({ processing: false, error: null });
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const session_id = m[1];
    // Strip the hash immediately so refresh doesn't re-run
    const url = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", url);

    setState({ processing: true, error: null });
    api
      .post("/auth/session", { session_id }, { withCredentials: true })
      .then(async () => {
        await refresh();
        setState({ processing: false, error: null });
      })
      .catch((e) => {
        setState({ processing: false, error: e?.response?.data?.detail || "Auth failed." });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}
